import { useState, useMemo, useCallback, useEffect } from 'react';
import dayjs from '../../../config/dayjs';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';

export function useUserActivityHistory() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('non-pod'); // 'pod' | 'non-pod'
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDetailUser, setSelectedDetailUser] = useState(null);
  const [searchDetail, setSearchDetail] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  // Reset selected users and data when switching tab
  useEffect(() => {
    setSelectedUsers([]);
    setData([]);
    setSelectedDetailUser(null);
    setSearchDetail('');
    setWarningMessage('');
  }, [activeTab]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
    setData([]);
    setSelectedDetailUser(null);
    setSearchDetail('');
    setWarningMessage('');
  }, []);

  const handleUsersChange = useCallback((newUsers) => {
    setSelectedUsers(newUsers);
  }, []);

  const fetchActivityHistory = useCallback(async () => {
    if (!selectedDate || selectedUsers.length === 0) return;

    setLoading(true);
    setWarningMessage('');

    try {
      const isPod = activeTab === 'pod';
      const requests = selectedUsers.map((userId) =>
        fleetApi.get('/user-activity-history', {
          params: { date: selectedDate, userId, isPod }
        })
      );

      const results = await Promise.allSettled(requests);

      const successData = [];
      results.forEach((r) => {
        if (r.status === 'fulfilled' && r.value) {
          const val = r.value;
          // Extract payload from response wrapper if any
          let payload = val.data !== undefined ? val.data : val;
          if (payload && payload.data !== undefined && !payload.username && !payload.userId && !payload.user_id) {
            payload = payload.data;
          }

          if (Array.isArray(payload)) {
            payload.forEach((item) => {
              if (item && typeof item === 'object') {
                successData.push(item);
              }
            });
          } else if (payload && typeof payload === 'object') {
            successData.push(payload);
          }
        }
      });

      setData(successData);

      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        setWarningMessage(
          `${failedCount} ${t('userActivity.of', 'of')} ${selectedUsers.length} ${t('userActivity.usersFailedToLoad', 'users failed to load data.')}`
        );
      }
    } catch (error) {
      console.error('Error fetching user activity history:', error);
      setWarningMessage(t('userActivity.fetchError', 'An error occurred while fetching user activity history data.'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedDate, selectedUsers, t]);

  const filteredEndpointHistory = useMemo(() => {
    if (!selectedDetailUser) return [];

    const list = selectedDetailUser.endpointHistory || selectedDetailUser.endpoint_history || selectedDetailUser.endpoints || [];

    if (!Array.isArray(list)) return [];
    if (!searchDetail.trim()) return list;

    const term = searchDetail.trim().toLowerCase();
    return list.filter((item) => {
      const endpoint = String(item?.endpoint || item?.path || item?.url || '').toLowerCase();
      const method = String(item?.method || '').toLowerCase();
      const baseUrl = String(item?.baseUrl || item?.base_url || '').toLowerCase();
      const statusMsg = String(
        item?.responseBody?.statusMessage ||
          item?.responseBody?.message ||
          item?.responseBody?.statusCode ||
          item?.response_body?.statusMessage ||
          item?.response_body?.message ||
          item?.response_body?.statusCode ||
          ''
      ).toLowerCase();

      return endpoint.includes(term) || method.includes(term) || baseUrl.includes(term) || statusMsg.includes(term);
    });
  }, [selectedDetailUser, searchDetail]);

  const openDetail = useCallback((user) => {
    setSelectedDetailUser(user);
    setSearchDetail('');
  }, []);

  const closeDetail = useCallback(() => {
    setSelectedDetailUser(null);
    setSearchDetail('');
  }, []);

  return {
    activeTab,
    setActiveTab: handleTabChange,
    selectedDate,
    setSelectedDate: handleDateChange,
    selectedUsers,
    setSelectedUsers: handleUsersChange,
    loading,
    data,
    selectedDetailUser,
    setSelectedDetailUser,
    searchDetail,
    setSearchDetail,
    filteredEndpointHistory,
    fetchActivityHistory,
    openDetail,
    closeDetail,
    warningMessage
  };
}
